import { useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import SimCardDownloadOutlinedIcon from "@mui/icons-material/SimCardDownloadOutlined";
import {
  Box,
  Stack,
  Button,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
  Typography,
  IconButton,
} from "@financeable-com-au/financeable-ui";
import { APPLICATION_DOWNLOAD_TYPE } from "@/constants";

export type ApplicationDownloadType = (typeof APPLICATION_DOWNLOAD_TYPE)[keyof typeof APPLICATION_DOWNLOAD_TYPE];

/** Formats offered in the caret menu. Add a format here and every download button picks it up. */
const DOWNLOAD_FORMATS: { label: string; type: ApplicationDownloadType }[] = [
  { label: "PDF", type: APPLICATION_DOWNLOAD_TYPE.PDF },
  { label: "Excel", type: APPLICATION_DOWNLOAD_TYPE.EXCEL },
];

// Visually fuse the main button and the caret trigger into a single split button.
// (We can't use `ButtonGroup` here: it clones its children and injects group props onto
// the `Dropdown`, which breaks the menu trigger and sets `pointer-events: none`.)
// The design-system `Button` doesn't accept `sx`, so the left button is styled via its `Box` wrapper.
const mainButtonWrapperSx = { "& > button": { borderTopRightRadius: 0, borderBottomRightRadius: 0 } } as const;
const caretButtonSx = { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, ml: "-1px" } as const;

interface DownloadApplicationButtonProps {
  /** Format selected initially — typically the org's configured default. */
  defaultDownloadType: ApplicationDownloadType;
  /** Called only when the main button is clicked, with the currently selected format. */
  onDownload: (downloadType: ApplicationDownloadType) => void;
  isLoading?: boolean;
  disabled?: boolean;
  label?: string;
  loadingLabel?: string;
}

/**
 * Split button for downloading an application. The caret menu only *selects* the format (PDF / Excel);
 * the download is triggered solely by clicking the main button, using the selected format.
 */
const DownloadApplicationButton = ({
  defaultDownloadType,
  onDownload,
  isLoading = false,
  disabled = false,
  label = "Download Application",
  loadingLabel = "Downloading...",
}: DownloadApplicationButtonProps) => {
  const [selectedType, setSelectedType] = useState<ApplicationDownloadType>(defaultDownloadType);
  const isDisabled = disabled || isLoading;
  const selectedFormat = DOWNLOAD_FORMATS.find((format) => format.type === selectedType) ?? DOWNLOAD_FORMATS[0];

  return (
    <Stack flexDirection="row" alignItems="center" columnGap={1}>
      <Stack flexDirection="row" alignItems="stretch">
        <Box sx={mainButtonWrapperSx}>
          <Button
            variant="secondary"
            intent="neutral"
            size="sm"
            disabled={isDisabled}
            endDecorator={<SimCardDownloadOutlinedIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => onDownload(selectedType)}
          >
            {isLoading ? loadingLabel : label}
          </Button>
        </Box>
        <Dropdown>
          <MenuButton
            variant="outlined"
            color="neutral"
            size="sm"
            disabled={isDisabled}
            aria-label={`Download format: ${selectedFormat.label}. Choose download format`}
            endDecorator={<ArrowDropDownIcon sx={{ fontSize: "1rem" }} />}
            sx={caretButtonSx}
          >
            {selectedFormat.label}
          </MenuButton>
          <Menu size="sm" placement="bottom-end">
            {DOWNLOAD_FORMATS.map(({ label: formatLabel, type }) => {
              const isSelected = selectedType === type;
              return (
                <MenuItem key={type} selected={isSelected} onClick={() => setSelectedType(type)}>
                  <Box
                    component="span"
                    sx={{ width: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  >
                    {isSelected && <CheckIcon sx={{ fontSize: "1rem" }} />}
                  </Box>
                  {formatLabel}
                </MenuItem>
              );
            })}
          </Menu>
        </Dropdown>
      </Stack>
      <Tooltip
        title={
          <Stack padding={2} rowGap={1} sx={{ maxWidth: 240 }}>
            <Typography level="body-sm" bold>
              Choose your download format
            </Typography>
            <Typography level="body-sm">
              Use the arrow to select PDF or Excel, then click Download Application to download it in that format.
            </Typography>
          </Stack>
        }
      >
        <IconButton aria-label="Download format help" isRounded={true} size="sm" variant="inline">
          <InfoIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export { DownloadApplicationButton };
